/**
 * Document edge detection — finds the paper contour in a receipt
 * photo and applies a perspective transform so the cropped image is
 * a rectangular receipt instead of a phone-angled photo of paper on
 * a desk.
 *
 * Pure OpenCV.js — no jscanify or other wrapper needed (the jscanify
 * npm/CDN trail kept 404'ing during setup, so we inline the
 * 100-line algorithm directly here). OpenCV.js is loaded lazily on
 * first call via a <script> tag pointing at /opencv/opencv.js (see
 * mobile/public/opencv/README.md for setup).
 *
 *   const cropped = await detectAndCrop(blob);
 *   if (cropped) upload(cropped);
 *   else         upload(blob);   // fallback to the original photo
 *
 * Failures (no quadrilateral, opencv missing, error mid-detection)
 * all resolve to `null`. The caller is expected to fall back to the
 * original photo silently — this is a best-effort enhancement, not
 * a gate on saving the receipt.
 */

// ── Dev-only logger ───────────────────────────────────────────────────
//
// The detection pipeline emits a lot of step-by-step diagnostic logs
// (script injection, WASM-ready, image decode, contour stats, corner
// counts, etc.). These are invaluable while iterating but production
// noise once shipped. `dlog` no-ops in prod builds; real failures
// continue to fire `console.warn` outside this gate.
const DEV = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
const dlog = DEV
  ? (...args) => console.log(...args)
  : () => {};

// ── OpenCV.js loader ───────────────────────────────────────────────────
//
// Vendored at /opencv/opencv.js. We load it via a <script> tag rather
// than an ES import because OpenCV.js attaches to window.cv at
// runtime — bundlers don't help with that.
//
// `cv` becomes useful only after the WASM has compiled + linked. We
// poll for cv.Mat to be ready, which is more robust than the runtime
// init callback alone (the callback fires once and won't re-fire on
// a later import).

let _cvPromise = null;

function loadOpenCv() {
  if (_cvPromise) return _cvPromise;
  _cvPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('OpenCV requires a browser environment'));
      return;
    }
    if (window.cv && window.cv.Mat) {
      resolve(window.cv);
      return;
    }

    const POLL_MS = 200;
    // 30s — opencv.js is ~9.9MB and the WASM init alone can take
    // 5-10s on a low-end Android. First-capture load over mobile
    // data has been observed to need the full 30s.
    const TIMEOUT_MS = 30_000;

    function pollUntilReady(start) {
      // We need WASM to be fully initialised, not just cv.Mat to be
      // defined as a JS shim. The reliable test is to actually
      // instantiate a Mat — that exercises the WASM path and throws
      // synchronously if the runtime isn't ready yet.
      if (window.cv && typeof window.cv.Mat === 'function') {
        try {
          const probe = new window.cv.Mat();
          probe.delete();
          dlog(
            '[doc-scan] OpenCV ready (WASM verified) after',
            Date.now() - start, 'ms · cv has .then:',
            typeof window.cv.then,
          );
          // Emscripten exposes Module as a thenable — if we resolve
          // with `window.cv` directly, the awaiter's promise auto-
          // chains into `Module.then(...)` which doesn't fire the
          // way we expect. Wrap in an array (not thenable) so the
          // resolve passes through unchanged.
          resolve([window.cv]);
          return;
        } catch (probeErr) {
          dlog('[doc-scan] cv.Mat exists but WASM not ready, polling…');
        }
      }
      if (Date.now() - start > TIMEOUT_MS) {
        reject(new Error(`OpenCV took longer than ${TIMEOUT_MS}ms to initialize`));
        return;
      }
      setTimeout(() => pollUntilReady(start), POLL_MS);
    }

    const existing = document.querySelector('script[data-opencv]');
    if (existing) {
      dlog('[doc-scan] OpenCV script already injected, polling for cv.Mat');
      pollUntilReady(Date.now());
      return;
    }
    dlog('[doc-scan] injecting opencv.js script tag');
    const script = document.createElement('script');
    script.async = true;
    script.src = '/opencv/opencv.js';
    script.dataset.opencv = '1';
    script.onload = () => {
      dlog('[doc-scan] opencv.js script onload — polling for cv.Mat');
      pollUntilReady(Date.now());
    };
    script.onerror = () =>
      reject(new Error('Failed to load /opencv/opencv.js — see public/opencv/README.md'));
    document.head.appendChild(script);
  });
  return _cvPromise;
}

// ── Helpers ───────────────────────────────────────────────────────────

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode photo'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

/**
 * Order four detected corners as { topLeft, topRight, bottomRight,
 * bottomLeft } so the perspective transform maps them onto the same
 * positions in the output rectangle. Algorithm: sort by sum/diff of
 * coordinates — TL has min(x+y), BR has max(x+y), TR has min(y-x),
 * BL has max(y-x).
 */
function orderCorners(pts) {
  const sums  = pts.map((p) => p.x + p.y);
  const diffs = pts.map((p) => p.y - p.x);
  return {
    topLeft:     pts[sums.indexOf(Math.min(...sums))],
    bottomRight: pts[sums.indexOf(Math.max(...sums))],
    topRight:    pts[diffs.indexOf(Math.min(...diffs))],
    bottomLeft:  pts[diffs.indexOf(Math.max(...diffs))],
  };
}

/**
 * Find the largest 4-sided polygon-shaped contour in `gray` that
 * looks like a piece of paper. Returns the four corner points
 * (unordered) or null if no plausible quadrilateral was found.
 *
 * Algorithm (the standard "scanner" recipe):
 *   1. Blur a bit to soften noise.
 *   2. Canny edge detection (50/150 thresholds — values that work
 *      well on receipt photos in mixed lighting).
 *   3. Dilate edges so near-broken contours close up.
 *   4. findContours, RETR_EXTERNAL so we ignore inner edges.
 *   5. Sort contours by area descending; iterate.
 *   6. approxPolyDP at 2% of perimeter — receipts approximate to a
 *      4-vertex polygon when the photo is reasonably square-on.
 *   7. First 4-vertex contour with area ≥ 25% of the image area is
 *      the paper. The 25% floor rules out small in-frame artefacts
 *      that happen to be quadrilateral.
 */
function findPaperCorners(cv, gray) {
  const blurred = new cv.Mat();
  const edges = new cv.Mat();
  const dilated = new cv.Mat();
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();

  let result = null;
  try {
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);
    // Lowered Canny thresholds (was 50/150) to catch fainter receipt
    // edges — works better for low-contrast or sub-optimally lit
    // photos. Cost: more noise contours to filter out, which the
    // 4-vertex + min-area gate handles fine.
    cv.Canny(blurred, edges, 30, 100);

    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
    cv.dilate(edges, dilated, kernel);
    kernel.delete();

    cv.findContours(
      dilated,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE,
    );

    const indexedAreas = [];
    for (let i = 0; i < contours.size(); i++) {
      const c = contours.get(i);
      indexedAreas.push({ i, area: cv.contourArea(c) });
    }
    indexedAreas.sort((a, b) => b.area - a.area);

    const imgArea = gray.rows * gray.cols;
    // Lowered min-area floor (was 0.20) so close-up receipts that
    // fill a smaller portion of the frame still qualify.
    const minArea = imgArea * 0.10;
    dlog(
      '[doc-scan] findPaperCorners: image area =',
      imgArea,
      '| contours found =',
      contours.size(),
      '| top 5 areas =',
      indexedAreas.slice(0, 5).map((x) => Math.round(x.area)),
      '| min-area gate =',
      Math.round(minArea),
    );

    let attempted = 0;
    for (const { i, area } of indexedAreas) {
      if (area < minArea) break;
      attempted++;
      const c = contours.get(i);
      const peri = cv.arcLength(c, true);

      // 1) Apply a convex hull first to smooth out wrinkles and
      //    creases. A crumpled receipt has a rectangular hull but
      //    its raw contour traces every fold, producing too many
      //    vertices when we approximate.
      // Allocate the hull Mat OUTSIDE the inner try so the finally
      // can clean it up even if convexHull itself throws on a
      // degenerate contour (rare but possible).
      const hull = new cv.Mat();
      let approxResult = null;
      try {
        cv.convexHull(c, hull, false, true);

        // 2) Try a sequence of approximation tolerances. The hull
        //    fixes most cases at the standard 0.02 epsilon, but
        //    really wrinkled paper sometimes needs more aggressive
        //    simplification.
        for (const epsRatio of [0.02, 0.04, 0.06, 0.08]) {
          const approx = new cv.Mat();
          try {
            cv.approxPolyDP(hull, approx, epsRatio * peri, true);
            dlog(
              '[doc-scan]   contour #' + i,
              'area =', Math.round(area),
              '(hull) eps =', epsRatio,
              'vertices =', approx.rows,
            );
            if (approx.rows === 4) {
              const data = approx.data32S;
              approxResult = [
                { x: data[0], y: data[1] },
                { x: data[2], y: data[3] },
                { x: data[4], y: data[5] },
                { x: data[6], y: data[7] },
              ];
              break;
            }
          } finally {
            approx.delete();
          }
        }

        // 3) If iteration didn't produce a 4-vertex polygon, fall
        //    back to the minimum-area bounding rectangle. Always 4
        //    corners; slightly less precise (the rectangle may extend
        //    a few pixels into the background) but never fails on a
        //    contour with significant area.
        if (!approxResult) {
          const rect = cv.minAreaRect(hull);
          const points = cv.RotatedRect.points(rect);
          approxResult = points.map((p) => ({ x: p.x, y: p.y }));
          dlog(
            '[doc-scan]   contour #' + i,
            'falling back to minAreaRect — 4 corners from rotated bounding box',
          );
        }
      } finally {
        hull.delete();
      }

      if (approxResult) {
        result = approxResult;
        break;
      }
    }

    if (!result) {
      dlog(
        '[doc-scan]   no large-enough contour produced 4 corners (attempted =',
        attempted,
        ')',
      );
    }
  } finally {
    blurred.delete();
    edges.delete();
    dilated.delete();
    contours.delete();
    hierarchy.delete();
  }
  return result;
}

/**
 * Apply a perspective transform to extract the rectangle defined by
 * `corners` from `src`. Output is sized so the natural aspect ratio
 * of the detected quadrilateral is preserved (no squashing).
 *
 * Returns an HTMLCanvasElement holding the corrected image.
 */
function extractPaper(cv, src, corners, maxOutputDim) {
  // Inset the detected quad slightly toward its centroid so the
  // crop doesn't include the few pixels of background that the
  // convex hull bulged out into. 2% on all sides loses a sliver of
  // receipt edge but reliably eliminates the background bleed
  // visible in the early-test PDFs.
  const cx = (corners.topLeft.x + corners.topRight.x +
              corners.bottomRight.x + corners.bottomLeft.x) / 4;
  const cy = (corners.topLeft.y + corners.topRight.y +
              corners.bottomRight.y + corners.bottomLeft.y) / 4;
  const INSET = 0.02;
  const inset = (p) => ({
    x: p.x + (cx - p.x) * INSET,
    y: p.y + (cy - p.y) * INSET,
  });
  let { topLeft, topRight, bottomRight, bottomLeft } = {
    topLeft:     inset(corners.topLeft),
    topRight:    inset(corners.topRight),
    bottomRight: inset(corners.bottomRight),
    bottomLeft:  inset(corners.bottomLeft),
  };

  let widthA  = Math.hypot(bottomRight.x - bottomLeft.x, bottomRight.y - bottomLeft.y);
  let widthB  = Math.hypot(topRight.x    - topLeft.x,    topRight.y    - topLeft.y);
  let heightA = Math.hypot(topRight.x    - bottomRight.x, topRight.y    - bottomRight.y);
  let heightB = Math.hypot(topLeft.x     - bottomLeft.x,  topLeft.y     - bottomLeft.y);

  let naturalWidth  = Math.max(widthA,  widthB);
  let naturalHeight = Math.max(heightA, heightB);

  // Receipts are essentially always portrait. If the corner ordering
  // produced a landscape-oriented output (typical when the original
  // photo had the receipt rotated, like the PUMA test sample), rotate
  // the mapping 90° so the longer edge becomes the output height.
  // Visually: the previous TL→TR edge becomes the new TR→BR edge.
  if (naturalWidth > naturalHeight) {
    dlog('[doc-scan]   landscape output detected — rotating to portrait');
    const rotated = {
      topLeft:     bottomLeft,
      topRight:    topLeft,
      bottomRight: topRight,
      bottomLeft:  bottomRight,
    };
    topLeft     = rotated.topLeft;
    topRight    = rotated.topRight;
    bottomRight = rotated.bottomRight;
    bottomLeft  = rotated.bottomLeft;
    [naturalWidth, naturalHeight] = [naturalHeight, naturalWidth];
  }

  // Cap longest edge at maxOutputDim to keep output blob small.
  const scale = Math.min(maxOutputDim / Math.max(naturalWidth, naturalHeight), 1);
  const outW = Math.max(1, Math.round(naturalWidth  * scale));
  const outH = Math.max(1, Math.round(naturalHeight * scale));

  const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
    topLeft.x,     topLeft.y,
    topRight.x,    topRight.y,
    bottomRight.x, bottomRight.y,
    bottomLeft.x,  bottomLeft.y,
  ]);
  const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
    0,      0,
    outW,   0,
    outW,   outH,
    0,      outH,
  ]);

  const M = cv.getPerspectiveTransform(srcTri, dstTri);
  const dst = new cv.Mat();
  try {
    cv.warpPerspective(
      src,
      dst,
      M,
      new cv.Size(outW, outH),
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(),
    );

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    cv.imshow(canvas, dst);
    return canvas;
  } finally {
    srcTri.delete();
    dstTri.delete();
    M.delete();
    dst.delete();
  }
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Detect a paper-receipt quadrilateral in `blob` and return a
 * perspective-corrected JPEG Blob, or null if detection didn't find
 * a viable quadrilateral. Always returns null on any error — never
 * throws.
 *
 * @param {Blob} blob  the source photo (typically already compressed)
 * @param {{ maxOutputDim?: number, quality?: number }} [opts]
 *        maxOutputDim — cap on the longer edge of the output canvas
 *                       (default 1600). Output keeps the detected
 *                       receipt's natural aspect ratio.
 *        quality — JPEG quality for the output blob (default 0.8).
 */
export async function detectAndCrop(blob, { maxOutputDim = 1600, quality = 0.8 } = {}) {
  let url;
  let cv;
  let src;
  let gray;
  const t0 = Date.now();
  const log = (...args) => dlog('[doc-scan]', `+${Date.now() - t0}ms`, ...args);

  try {
    log('start, blob size:', blob?.size);
    log('about to await loadOpenCv()');
    // loadOpenCv resolves with a single-element array [cv] to avoid
    // the auto-chain into Emscripten's thenable Module — see the
    // pollUntilReady comment for context.
    const [resolvedCv] = await loadOpenCv();
    cv = resolvedCv;
    log('await resolved, cv typeof:', typeof cv, 'has Mat:', !!cv?.Mat);

    log('about to decode blob');
    const decoded = await blobToImage(blob);
    url = decoded.url;
    log('image decoded:', decoded.img.naturalWidth, 'x', decoded.img.naturalHeight);

    log('drawing image to source canvas');
    const srcCanvas = document.createElement('canvas');
    srcCanvas.width  = decoded.img.naturalWidth;
    srcCanvas.height = decoded.img.naturalHeight;
    srcCanvas.getContext('2d').drawImage(decoded.img, 0, 0);
    log('canvas drawn');

    log('cv.imread starting');
    src = cv.imread(srcCanvas);
    log('cv.imread done, src dims:', src?.cols, 'x', src?.rows);

    log('cvtColor to grey starting');
    gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    log('greyscale ready');

    log('findPaperCorners starting');
    const corners = findPaperCorners(cv, gray);
    if (!corners) {
      log('findPaperCorners returned no quadrilateral');
      return null;
    }
    log('corners found:', corners);

    const ordered = orderCorners(corners);

    const widthA  = Math.hypot(ordered.bottomRight.x - ordered.bottomLeft.x,
                               ordered.bottomRight.y - ordered.bottomLeft.y);
    const heightA = Math.hypot(ordered.topRight.x    - ordered.bottomRight.x,
                               ordered.topRight.y    - ordered.bottomRight.y);
    if (Math.max(widthA, heightA) < 200) {
      log('rejected — output dimensions too small');
      return null;
    }

    const resultCanvas = extractPaper(cv, src, ordered, maxOutputDim);
    log('perspective transform done');
    const out = await canvasToBlob(resultCanvas, 'image/jpeg', quality);
    log('cropped blob ready, size:', out?.size);
    return out;
  } catch (err) {
    console.warn('[doc-scan] failed at +', Date.now() - t0, 'ms:', err);
    return null;
  } finally {
    if (url) URL.revokeObjectURL(url);
    src?.delete?.();
    gray?.delete?.();
  }
}
